import { useForm, Link, router } from "@inertiajs/react";
import AppShell from "../../components/Shell/AppShell";
import { common, pick } from "../../lib/i18n";
import type { CatalogSummary, CurrentUser, ProductSummary } from "../../types";

const copy = {
  pl: { confirmDelete: (n: string) => `Usunąć katalog „${n}"?`, title: "Katalogi", subtitle: "Składaj brandowane katalogi produktowe z gotowych grafik i danych produktu.", catalogName: "Nazwa katalogu", namePlaceholder: "np. Katalog foteli biurowych 2026", newCatalog: "+ Nowy katalog", noCatalogs: "Brak katalogów", noCatalogsHint: "Utwórz pierwszy katalog, żeby złożyć produkty w gotowy do druku dokument.", productWord: (n: number): string => n === 1 ? "produkt" : "produktów" },
  en: { confirmDelete: (n: string) => `Delete catalog "${n}"?`, title: "Catalogs", subtitle: "Assemble branded product catalogs from ready visuals and product data.", catalogName: "Catalog name", namePlaceholder: "e.g. Office chairs catalog 2026", newCatalog: "+ New catalog", noCatalogs: "No catalogs", noCatalogsHint: "Create your first catalog to assemble products into a print-ready document.", productWord: (n: number): string => n === 1 ? "product" : "products" },
};

type Props = {
  catalogs: CatalogSummary[];
  current_user: CurrentUser;
  products?: ProductSummary[];
};

export default function CatalogsIndex({ catalogs, current_user, products = [] }: Props) {
  const initials = current_user.name.split(" ").map((word) => word[0]).join("").slice(0, 2).toUpperCase();
  const canEdit = current_user.role === "admin" || current_user.role === "moderator";
  const t = pick(copy);
  const c = common();
  const { data, setData, post, processing, reset } = useForm({ name: "" });

  const createCatalog = (event: React.FormEvent) => {
    event.preventDefault();
    post("/catalogs", { onSuccess: () => reset("name") });
  };

  const removeCatalog = (catalog: CatalogSummary) => {
    if (!window.confirm(t.confirmDelete(catalog.name))) return;
    router.delete(`/catalogs/${catalog.id}`);
  };

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="page">
        <div className="page-hd">
          <div>
            <h2>{t.title}</h2>
            <p>{t.subtitle}</p>
          </div>
        </div>

        {canEdit && (
          <form onSubmit={createCatalog} className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-body" style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <div className="field" style={{ flex: 1, margin: 0 }}>
                <label>{t.catalogName}</label>
                <input
                  className="input"
                  value={data.name}
                  onChange={(event) => setData("name", event.target.value)}
                  placeholder={t.namePlaceholder}
                  maxLength={120}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={processing}>
                {t.newCatalog}
              </button>
            </div>
          </form>
        )}

        {catalogs.length === 0 ? (
          <div className="empty">
            <h4>{t.noCatalogs}</h4>
            <p>{t.noCatalogsHint}</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
            {catalogs.map((catalog) => (
              <div key={catalog.id} className="panel" style={{ display: "flex", flexDirection: "column" }}>
                <div className="panel-body" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Link href={`/catalogs/${catalog.id}`} style={{ fontWeight: 600, fontSize: 15 }}>
                    {catalog.name}
                  </Link>
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>
                    {catalog.product_count} {t.productWord(catalog.product_count)}
                  </span>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <Link href={`/catalogs/${catalog.id}`} className="btn btn-sm">{c.actions.open}</Link>
                    {canEdit && (
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => removeCatalog(catalog)}>
                        {c.actions.delete}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
